<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { almConfig } from "@/almConfig";
import BaseButton from "@/components-ng/BaseButton.vue";
import BaseModal from "@/components-ng/BaseModal.vue";
import SearchInput from "@/components-ng/SearchInput.vue";
import SvgIcon from "@/components-ng/SvgIcon.vue";
import OrgEntityMembers from "@/components-ng/organization/OrgEntityMembers.vue";
import Badge from "@/components-ng/ui/Badge.vue";
import Tree from "@/components-ng/ui/Tree.vue";
import { useTreeUrlState } from "@/components-ng/ui/useTreeUrlState";
import { useSessionAlm } from "@/composables/useSessionAlm";
import SessionHandler from "@/handlers/sessionHandler";
import { TeamHandler } from "@/handlers/teamHandler";
import { UserHandler } from "@/handlers/userHandler";
import { captureExceptionWithContext } from "@/sentry";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";

import PiDetailsSummary from "./PiDetailsSummary.vue";

const props = defineProps({
  // A row off the sessions list - it already carries the ALM status the list
  // subscribed to. Null closes the modal.
  session: { type: Object, default: null },
});

const emit = defineEmits(["close"]);

const route = useRoute();
const store = useAppStore();

const teamHandler = new TeamHandler();
const userHandler = new UserHandler();

const sessionArts = ref([]);
// Granted access to the session without a team board of their own
const participatingTeams = ref([]);
const creator = ref(null);
const loading = ref(false);
// Distinguishes "this session has no ARTs" from "we could not find out"
const treeError = ref(false);

// Teams with a board here. A participating team is left out: nothing syncs it,
// so its mapping cannot be this session's problem.
const sessionTeamIds = computed(() =>
  sessionArts.value.flatMap((art) => (art.teams ?? []).map((team) => team.id)),
);
const sessionArtIds = computed(() => sessionArts.value.map((art) => art.id));

const {
  alm,
  erroredTeamIds,
  erroredArtIds,
  canCheck: canCheckMappings,
  checksUnsupported,
  checkTeamMappings,
  checkArtMappings,
} = useSessionAlm(() => props.session, {
  teamIds: () => sessionTeamIds.value,
  artIds: () => sessionArtIds.value,
});

// An external team's org holds no members, so there is nothing to select it for.
// `synced` is false for a team with no board here: its mapping cannot affect a
// session that never syncs it.
function teamNode(team, synced) {
  return {
    id: `team-${team.id}`,
    label: team.name,
    icon: team.external ? "persona/external-team" : "persona/team",
    hasError: synced && erroredTeamIds.value.includes(team.id),
    ...(team.external
      ? { selectable: false }
      : { entity: { type: "team", id: team.id, name: team.name } }),
  };
}

// Sessions predating the solution board report a pseudo-ART (id 0, "Teams") that
// no org endpoint resolves, so it only heads its teams
function artNode(art) {
  const legacy = !art.id;
  return {
    id: `art-${art.id}`,
    label: art.name,
    icon: legacy ? undefined : "persona/art",
    hasError: erroredArtIds.value.includes(art.id),
    count: art.teams?.length ?? 0,
    children: (art.teams ?? []).map((team) => teamNode(team, true)),
    ...(legacy ? { selectable: false } : { entity: { type: "art", id: art.id, name: art.name } }),
  };
}

// Flat by design: these come from the program boards' swimlanes, not the org, so
// a team can sit under an ART it does not organizationally belong to. No ST node
// either - a one-ART session has none, and nothing exposes the one the rest keep.
const treeNodes = computed(() => {
  const nodes = sessionArts.value.map(artNode);
  if (participatingTeams.value.length) {
    nodes.push({
      id: "participating-teams",
      label: "Teams with access (no board)",
      count: participatingTeams.value.length,
      children: participatingTeams.value.map((team) => teamNode(team, false)),
      selectable: false,
    });
  }
  return nodes;
});

const artCount = computed(() => sessionArts.value.length);

const {
  query,
  visibleItems,
  selected,
  selectedId,
  expandIds,
  select,
  reset,
  readUrl,
  restoreSelection,
} = useTreeUrlState(treeNodes, { writable: () => !!props.session });

// Monotonic guard: reopening on another session while the first is still in
// flight must not let the older answers land on the newer session.
let seq = 0;

watch(
  () => props.session,
  async (session) => {
    const mine = ++seq;
    sessionArts.value = [];
    participatingTeams.value = [];
    creator.value = null;
    treeError.value = false;
    reset();
    if (!session) return;

    // Re-read per opening: the modal is mounted for the life of the page
    readUrl();

    // Ahead of the tree and never awaited with it: it does not depend on the
    // tree, and behind it a slow or failing tree took it down too
    void loadCreator(session, mine);

    loading.value = true;
    try {
      const handler = new SessionHandler(createHandlerContext(), session.session_id);
      const [arts, participatingIds] = await Promise.all([
        handler.getArts(),
        handler.getParticipatingTeamIds(),
      ]);
      if (mine !== seq) return;
      sessionArts.value = arts ?? [];
      const participating = await resolveParticipating(arts, participatingIds);
      if (mine !== seq) return;
      participatingTeams.value = participating;

      restoreSelection();
    } catch (error) {
      // Recovered into a visible state: an empty tree would read as "no ARTs"
      if (mine === seq) treeError.value = true;
      void captureExceptionWithContext(error);
    } finally {
      if (mine === seq) loading.value = false;
    }
  },
  { immediate: true },
);

// Ids alone come back, so names cost a team list - fetched only when there is
// something to name. A team with a board is already in the tree under its ART.
async function resolveParticipating(arts, ids) {
  const inArts = new Set((arts ?? []).flatMap((art) => (art.teams ?? []).map((t) => t.id)));
  const extra = (ids ?? []).filter((id) => !inArts.has(id));
  if (!extra.length) return [];

  const res = await teamHandler.getTeams();
  const byId = new Map((res.data ?? []).map((team) => [team.id, team]));
  return extra.map((id) => byId.get(id)).filter(Boolean);
}

// Older sessions carry no `created_by`, and the id can outlive the user
async function loadCreator(session, mine) {
  if (!session.created_by) return;
  const res = await userHandler.getUser(session.created_by);
  if (mine === seq && res.success) creator.value = res.data ?? null;
}

// What `returnTo` reads, so a target's Save and Discard come back here
const from = computed(() => route.fullPath);

// The server's own rule: one ART means an ART session, two or more an ST session
const isOneArtSession = computed(() => sessionArts.value.length === 1);

// Admin-only, as the mapping pages are. Both params because the routes nest
// under the connection.
const mappingLink = computed(() => {
  const entity = selected.value;
  if (!entity || !alm.value || !store.isAdmin) return null;
  const params = { almType: alm.value.almType, connectionId: alm.value.connectionId };
  if (entity.type === "team") {
    return {
      name: "editAlmTeam",
      params: { ...params, teamId: `${entity.id}` },
      query: { from: from.value },
    };
  }
  return {
    name: "editAlmArt",
    params: { ...params, artId: `${entity.id}` },
    query: { from: from.value },
  };
});

// Jira keeps this per ART on the connection - except on a one-ART session, where
// it is keyed by session and only the ART Backlog Board step edits it.
const teamFieldMappingLink = computed(() =>
  almConfig(alm.value?.almType).fieldMapping &&
  isOneArtSession.value &&
  selected.value?.type === "art" &&
  props.session?.editable
    ? {
        path: "/edit-session",
        query: {
          path: "programBacklogBoard",
          id: props.session.session_id,
          tab: "teamFieldMapping",
          from: from.value,
        },
      }
    : null,
);
</script>

<template>
  <BaseModal
    :is-open="!!session"
    :title="`PI Details${session ? ` · ${session.name}` : ''}`"
    description="When this PI session was created and by whom, its ALM connection, and its ARTs and teams."
    size="wide"
    :body-padded="false"
    @close="emit('close')"
  >
    <div v-if="session" class="pi-details-modal">
      <div class="summary">
        <PiDetailsSummary
          :creation-date="session.creation_date"
          :creator
          :alm
          :can-open-events="store.isAdmin"
          :alm-pending="!session.almResolved && !session.connectionId"
        />
      </div>

      <div class="columns">
        <section class="tree-panel" aria-label="ARTs and teams">
          <div class="panel-toolbar">
            <h3 class="panel-heading">
              ARTs
              <Badge size="x-small">{{ artCount }}</Badge>
            </h3>
            <SearchInput v-model="query" placeholder="Search" class="search" />
          </div>
          <div class="panel-body">
            <p v-if="loading" class="hint">Loading…</p>
            <p v-else-if="treeError" class="hint">Could not load this session's ARTs and teams.</p>
            <p v-else-if="!visibleItems.length" class="hint">
              {{ query ? "No matches" : "This PI session has no ARTs yet." }}
            </p>
            <Tree
              v-else
              v-model:expand-ids="expandIds"
              :items="visibleItems"
              :selected-id
              aria-label="ARTs and teams"
              @select="select"
            >
              <template #suffix="{ node }">
                <SvgIcon
                  v-if="node.hasError"
                  name="system/alert-triangle"
                  width="16"
                  height="16"
                  class="node-error"
                  :tooltip="`${node.label} has ALM configuration errors`"
                />
                <Badge v-if="node.count" size="x-small" class="node-count">
                  {{ node.count }}
                </Badge>
              </template>
            </Tree>
          </div>
        </section>

        <OrgEntityMembers class="members-panel" :entity="selected">
          <template #actions>
            <router-link v-if="mappingLink" v-slot="{ href, navigate }" :to="mappingLink" custom>
              <BaseButton
                as="a"
                variant="text"
                size="small"
                icon-before="general/alm-connection"
                :href
                @click="navigate"
              >
                ALM mapping
              </BaseButton>
            </router-link>
            <router-link
              v-if="teamFieldMappingLink"
              v-slot="{ href, navigate }"
              :to="teamFieldMappingLink"
              custom
            >
              <BaseButton as="a" variant="text" size="small" :href @click="navigate">
                Team field mapping
              </BaseButton>
            </router-link>
          </template>
        </OrgEntityMembers>
      </div>
    </div>

    <template v-if="session && (canCheckMappings || checksUnsupported)" #actions>
      <template v-if="canCheckMappings">
        <BaseButton variant="outlined" size="small" @click="checkTeamMappings">
          Check team mapping errors
        </BaseButton>
        <BaseButton variant="outlined" size="small" @click="checkArtMappings">
          Check ART mapping errors
        </BaseButton>
      </template>
      <!-- On ADO and Rally an absent warning is not a clean bill of health -->
      <p v-else class="footer-hint">Mapping checks are available for Jira only</p>
    </template>
  </BaseModal>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

// 785px tall overall, minus BaseModal's own header (20 + 27 + 12)
$content-height: 726px;

.pi-details-modal {
  display: flex;
  flex-direction: column;
  height: $content-height;
}

.summary {
  padding: 4px 24px 16px;
  flex: none;
}

.columns {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  flex: 1;
  min-height: 0;
  border-top: 1px solid c.$border-light;
}

.tree-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid c.$border-light;
}

.panel-toolbar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px 4px;
}

.panel-heading {
  @include typography.small("semi-bold");

  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: c.$text;
}

.search {
  width: 100%;
}

.search :deep(.input) {
  box-sizing: border-box;
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 8px 16px;
}

.members-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;

  --members-cell-pad: 12px;
  --members-name-max: 170px;
  --members-email-max: 280px;
}

.members-panel :deep(.panel-body) {
  overflow-x: auto;
}

.node-error {
  color: c.$icon-warning-color;
  flex: none;
}

.node-count {
  flex: none;
}

.footer-hint {
  @include typography.small;

  margin: 0;
  margin-inline-end: auto;
  color: c.$text-subtle;
}

.hint {
  @include typography.small;

  margin: 0;
  padding: 8px;
  color: c.$text-subtle;
}
</style>
